import React, { useEffect, useState, useMemo } from 'react'
import { debounce } from '../../shared/utils/debounce'

export const BackgroundBlockPattern: React.FC = () => {
    const cellSize = 55
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight * 0.3, // 30% of viewport height
    })

    // Update on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight * 0.3,
            })
        }, 100)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const cols = Math.ceil(dimensions.width / cellSize)
    const rows = Math.ceil(dimensions.height / cellSize)

    // Randomly decide which cells are filled + their opacity variation
    const filledCells = useMemo(() => {
        const map = new Map<string, number>() // key: row-col, value: opacity
        for (let r = 0; r < rows; r++) {
            const chosenCols: number[] = []
            while (chosenCols.length < 1) {
                const c = Math.floor(Math.random() * cols)
                if (!chosenCols.includes(c)) chosenCols.push(c)
            }
            chosenCols.forEach(c => {
                const opacity = 0.05 + Math.random() * 0.15 // between 0.05–0.20
                map.set(`${r}-${c}`, opacity)
            })
        }
        return map
    }, [rows, cols])

    return (
        <svg
            width="100vw"
            height="30vh"
            viewBox={`0 0 ${cols * cellSize} ${rows * cellSize}`}
            preserveAspectRatio="xMidYMid slice"
            style={{
                position: 'absolute',
                left: '50%',
                right: '50%',
                bottom: 0,
                marginLeft: '-50vw',
                marginRight: '-50vw',
                display: 'block',
                background: 'transparent',
                zIndex: -1,
            }}
        >
            <defs>
                {/* Base gradient for each filled cell */}
                <linearGradient id="cellGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0C8B95" stopOpacity="1" />
                    <stop offset="100%" stopColor="#0C8B95" stopOpacity="0.6" />
                </linearGradient>

                {/* Fade the entire grid towards the top */}
                <linearGradient id="fadeTop" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="white" stopOpacity="0" />
                    <stop offset="60%" stopColor="white" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="white" stopOpacity="1" />
                </linearGradient>

                <mask id="topMask">
                    <rect width="100%" height="100%" fill="url(#fadeTop)" />
                </mask>
            </defs>

            <g mask="url(#topMask)">
                {Array.from({ length: rows }).map((_, row) =>
                    Array.from({ length: cols }).map((_, col) => {
                        const opacity = filledCells.get(`${row}-${col}`)
                        const isFilled = opacity !== undefined
                        return (
                            <rect
                                key={`${row}-${col}`}
                                x={col * cellSize}
                                y={row * cellSize}
                                width={cellSize}
                                height={cellSize}
                                fill={isFilled ? 'url(#cellGradient)' : 'white'}
                                fillOpacity={isFilled ? opacity : 1}
                                stroke="#0C8B95"
                                strokeOpacity={0.08}
                            />
                        )
                    })
                )}
            </g>
        </svg>
    )
}
